import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import api, { API_BASE } from '../api';
import { useAuth } from '../context/auth';
import { trackEvent } from '../analytics';
import Media from '../types/Media';
import { formatItemCount } from '../utils/listHelpers';

interface AddToListSheetProps {
  media: Media;
  onClose: () => void;
}

// ---------- styled ----------

var Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media (min-width: 601px) {
    align-items: center;
  }
`;

var Sheet = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  max-height: 80vh;
  width: 100%;
  max-width: 400px;
  overflow-y: auto;
  padding: 1.25rem;

  @media (min-width: 601px) {
    border-radius: 16px;
  }
`;

var SheetTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

var TitleText = styled.span`
  font-weight: 700;
  font-size: 1rem;
`;

var CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #999;
  cursor: pointer;
  padding: 0.25rem;
`;

var CreateNewButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: 1px dashed #ccc;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 0.75rem;

  &:hover {
    border-color: #999;
    color: #333;
  }
`;

var SectionLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.5rem;
`;

var ListRow = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border: none;
  background: none;
  color: #000;
  font-family: inherit;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  text-align: left;

  &:hover {
    background: ${(p) => (p.$disabled ? 'none' : '#f9f9f9')};
  }

  & + & {
    border-top: 1px solid #f0f0f0;
  }
`;

var ListThumb = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  background: #eee;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

var ListInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

var ListName = styled.div`
  font-weight: 700;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var ListMeta = styled.div`
  font-size: 0.72rem;
  color: #999;
  margin-top: 0.1rem;
`;

var CheckMark = styled.span`
  color: #2ecc71;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
`;

// ---------- new list form ----------

var NewListForm = styled.div`
  padding: 0.75rem 0;
`;

var FormInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #000;
  }
`;

var FormRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
  font-size: 0.8rem;
`;

var FormLabel = styled.span`
  font-weight: 600;
  color: #555;
  flex-shrink: 0;
`;

var OrderChip = styled.button<{ $active: boolean }>`
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? '#000' : '#ccc')};
  background: ${(p) => (p.$active ? '#000' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#333')};
  font-size: 0.7rem;
  cursor: pointer;
  font-weight: 600;
`;

var FormButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

var Toast = styled.div`
  position: fixed;
  /* Above the player bar, home indicator included. */
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 1300;
`;

// ---------- component ----------

interface MyList {
  id: string;
  list?: {
    title: string;
    kind: string;
    orderStyle: string;
    itemCount: number;
    items?: {
      id: string;
      media: { id: string; artwork_url: string; upc?: string | null; isrc?: string | null } | null;
    }[];
  };
  status?: string;
}

export default function AddToListSheet(props: AddToListSheetProps) {
  var media = props.media;
  var onClose = props.onClose;
  var authContext = useAuth();
  var authTokens = authContext.authTokens;

  var _lists = useState<MyList[]>([]);
  var lists = _lists[0];
  var setLists = _lists[1];

  var _loading = useState(true);
  var loading = _loading[0];
  var setLoading = _loading[1];

  var _showNewForm = useState(false);
  var showNewForm = _showNewForm[0];
  var setShowNewForm = _showNewForm[1];

  var _newTitle = useState('');
  var newTitle = _newTitle[0];
  var setNewTitle = _newTitle[1];

  var _creating = useState(false);
  var creating = _creating[0];
  var setCreating = _creating[1];

  var _toast = useState('');
  var toast = _toast[0];
  var setToast = _toast[1];

  var mediaKind = media.type === 'song' ? 'songs' : 'albums';

  // Drafts only, with every item: published lists are frozen, so offering one
  // only led to a rejected tap, and the duplicate check needs the whole list,
  // not the four-item preview my-lists returns by default.
  function loadLists() {
    return api.get('/api/lists/my-lists?status=unpublished&include=items').then(function (res) {
      setLists(res.data || []);
    });
  }

  useEffect(function () {
    loadLists()
      .catch(function () {})
      .then(function () {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(function () {
      setToast('');
    }, 2500);
  }

  // A song or album page loaded straight from the Apple catalog has no row in
  // our media table yet, so there is no id to add. Create it first, the same
  // way the list composer and CreatePost do, and reuse the id after that.
  //
  // The request itself is shared, not just its result: two quick taps on a
  // catalog page would otherwise each create the media, leaving two rows for
  // one album and the two lists pointing at different ones.
  var mediaIdRef = useRef<string | null>(media.id || null);
  var mediaRequestRef = useRef<Promise<string> | null>(null);

  function ensureMediaId(): Promise<string> {
    if (mediaIdRef.current) return Promise.resolve(mediaIdRef.current);
    if (mediaRequestRef.current) return mediaRequestRef.current;
    mediaRequestRef.current = api
      .post('/api/media', media)
      .then(function (res: any) {
        var id = res.data?.id;
        if (!id) throw new Error('media create returned no id');
        mediaIdRef.current = id;
        return id;
      })
      .catch(function () {
        mediaRequestRef.current = null;
        // The media endpoint's own reason is a bare "Bad Request" (it needs a
        // UPC or ISRC to match on), which means nothing to the person tapping,
        // so drop it and let the caller show its own message.
        throw new Error('media create failed');
      });
    return mediaRequestRef.current;
  }

  // Matched on the catalog identifier as well as the id. Album pages build
  // their media fresh each load without an id, so after the sheet is closed
  // and reopened only the UPC (albums) or ISRC (songs) can tell that the item
  // is already in a list.
  function isInList(list: MyList) {
    var id = mediaIdRef.current;
    var upc = media.type === 'album' ? media.upc : null;
    var isrc = media.type === 'song' ? media.isrc : null;
    return (list.list?.items || []).some(function (item) {
      var m = item.media;
      if (!m) return false;
      return (!!id && m.id === id) || (!!upc && m.upc === upc) || (!!isrc && m.isrc === isrc);
    });
  }

  // Lists with an add in flight, so a second tap on the same row does not
  // send a second request before the first one marks it as added.
  var _pending = useState<string[]>([]);
  var pending = _pending[0];
  var setPending = _pending[1];

  function setListPending(listId: string, on: boolean) {
    setPending(function (prev) {
      var rest = prev.filter(function (x) {
        return x !== listId;
      });
      return on ? rest.concat([listId]) : rest;
    });
  }

  function handleAddToList(list: MyList) {
    if (!list.list || isInList(list) || pending.indexOf(list.id) !== -1) return;
    setListPending(list.id, true);

    ensureMediaId()
      .then(function (mediaId) {
        return api.post('/api/lists/' + list.id + '/items', { mediaId: mediaId }).then(function () {
          trackEvent('list_item_added', {
            list_id: list.id,
            source: 'feed',
            list_status: list.status || 'unknown',
          });
          showToast('Added to "' + (list.list?.title || 'list') + '"');
          setLists(function (prev) {
            return prev.map(function (l) {
              if (l.id === list.id && l.list) {
                return Object.assign({}, l, {
                  list: Object.assign({}, l.list, {
                    itemCount: l.list.itemCount + 1,
                    items: (l.list.items || []).concat([
                      {
                        id: 'new',
                        media: {
                          id: mediaId,
                          artwork_url: media.artwork_url,
                          upc: media.upc,
                          isrc: media.isrc,
                        },
                      },
                    ]),
                  }),
                });
              }
              return l;
            });
          });
        });
      })
      .catch(function (err) {
        var msg = err?.response?.data?.reason || 'Could not add to list';
        showToast(msg);
      })
      .then(function () {
        setListPending(list.id, false);
      });
  }

  function handleCreateNew() {
    if (!newTitle.trim() || creating) return;
    setCreating(true);
    var title = newTitle.trim();

    // One request with the item in it, so success means the list exists AND
    // holds the item. It used to create an empty list and add the item
    // separately, reporting success even when the add failed.
    ensureMediaId()
      .then(function (mediaId) {
        return api.post('/api/lists', {
          user: { id: authTokens?.user?.id || '' },
          title: title,
          kind: mediaKind,
          orderStyle: 'playlist',
          items: [{ mediaId: mediaId }],
        });
      })
      .then(function () {
        trackEvent('list_created', {
          item_count: 1,
          kind: mediaKind,
          order_style: 'playlist',
          has_text: false,
          status: 'unpublished',
        });
        showToast('Created "' + title + '" with 1 ' + (media.type === 'song' ? 'song' : 'album'));
        setCreating(false);
        setShowNewForm(false);
        setNewTitle('');
        loadLists().catch(function () {});
      })
      .catch(function (err) {
        showToast(err?.response?.data?.reason || 'Could not create list');
        setCreating(false);
      });
  }

  // Filter lists to matching kind
  var compatibleLists = lists.filter(function (l) {
    return l.list && l.list.kind === mediaKind;
  });

  return (
    <>
      <Overlay
        onClick={function (e) {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <Sheet
          onClick={function (e) {
            e.stopPropagation();
          }}
        >
          <SheetTitle>
            <TitleText>Add to list</TitleText>
            <CloseButton onClick={onClose}>x</CloseButton>
          </SheetTitle>

          <CreateNewButton
            onClick={function () {
              setShowNewForm(!showNewForm);
            }}
          >
            + Create new list
          </CreateNewButton>

          {showNewForm && (
            <NewListForm>
              <FormInput
                type="text"
                placeholder="List title"
                value={newTitle}
                onChange={function (e) {
                  setNewTitle(e.target.value.slice(0, 120));
                }}
                autoFocus
              />
              <FormButton onClick={handleCreateNew} disabled={!newTitle.trim() || creating}>
                {creating ? 'Creating...' : 'Create List'}
              </FormButton>
            </NewListForm>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>Loading...</div>
          )}

          {!loading && compatibleLists.length > 0 && (
            <>
              <SectionLabel>Your Lists</SectionLabel>
              {compatibleLists.map(function (list) {
                var items = list.list?.items || [];
                var alreadyIn = isInList(list);
                var isPending = pending.indexOf(list.id) !== -1;
                var thumbItems = items.slice(0, 4);

                return (
                  <ListRow
                    key={list.id}
                    $disabled={alreadyIn || isPending}
                    onClick={function () {
                      if (!alreadyIn && !isPending) handleAddToList(list);
                    }}
                  >
                    <ListThumb>
                      {thumbItems.map(function (item, i) {
                        return item.media && item.media.artwork_url ? (
                          <img
                            key={i}
                            src={item.media.artwork_url
                              .replace('{w}x{h}', '100x100')
                              .replace('{w}', '100')
                              .replace('{h}', '100')}
                            alt=""
                          />
                        ) : null;
                      })}
                    </ListThumb>
                    <ListInfo>
                      <ListName>{list.list?.title}</ListName>
                      <ListMeta>
                        {list.status === 'unpublished' ? 'unpublished' : 'published'} &middot;{' '}
                        {formatItemCount(list.list?.itemCount || 0, list.list?.kind || mediaKind)}
                      </ListMeta>
                    </ListInfo>
                    {alreadyIn && <CheckMark>&#10003;</CheckMark>}
                  </ListRow>
                );
              })}
            </>
          )}

          {!loading && compatibleLists.length === 0 && !showNewForm && (
            <div
              style={{ textAlign: 'center', padding: '1rem', color: '#999', fontSize: '0.85rem' }}
            >
              No draft {mediaKind === 'songs' ? 'song' : 'album'} lists. Published lists are locked,
              so start a new one above.
            </div>
          )}
        </Sheet>
      </Overlay>

      {toast && <Toast>{toast}</Toast>}
    </>
  );
}
